import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@platform/api";
import { rfpQueryKeys } from "@platform/hooks";

import { RfpClientDetailsResponse } from "../types/rfpApi";

interface UseRfpClientDetailsOptions<TSelected> {
  rfpId: string;
  enabled?: boolean;
  select?: (data: RfpClientDetailsResponse) => TSelected;
}

// Use shared query keys for cache consistency
export const getQueryKey = rfpQueryKeys.clientDetails;

// useRfpClientDetails with selector overload
export function useRfpClientDetails<TSelected>(options: {
  rfpId: string;
  enabled?: boolean;
  select: (data: RfpClientDetailsResponse) => TSelected;
}): UseQueryResult<TSelected, Error>;

// useRfpClientDetails with default select for all RFP details response data
export function useRfpClientDetails(options: {
  rfpId: string;
  enabled?: boolean;
}): UseQueryResult<RfpClientDetailsResponse, Error>;

// useRfpClientDetails fetches client details given an RFP ID
// Pass in a React Query select to transform the data and store in RQ's cache
export function useRfpClientDetails<TSelected = RfpClientDetailsResponse>({
  rfpId,
  enabled = true,
  select,
}: UseRfpClientDetailsOptions<TSelected>): UseQueryResult<TSelected, Error> {
  const axios = useApi();
  return useQuery({
    queryKey: getQueryKey(rfpId),
    enabled,
    select,
    queryFn: () =>
      axios
        .get<RfpClientDetailsResponse>(`brokerportal/v1/rfp/${rfpId}/client`)
        .then((res) => res.data),
  });
}
